<template>
  <div class="signature-board gutter pb-0" :class="{'sticky': options.sticky}">
    <!--Single-->
    <template v-if="options.seperate">
      <div class="item" :class="{'full': options.fullWidth}" v-for="(signature, index) in getSignatures">
        <div class="actions">
          <div class="action mb-3">
            <div class="custom-control custom-checkbox">
              <input
                :id="'check' + index"
                :value="signature"
                v-model="checked"
                @click="$emit('checked-event', {'signature': signature})"
                type="checkbox"
                class="custom-control-input"
              >
              <label class="custom-control-label" :for="'check' + index"></label>
            </div>
          </div>
          <actions :signature-id="signature.id" :multiple="false"></actions>
        </div>
        <div class="svg single">
          <svg :ref="signature.id" :key="signature.id"></svg>
          <slider :signature-id="signature.id" :key="'slider' + signature.id"></slider>
        </div>
        <signature-dimension :signature-id="signature.id" :dimensions="dimensions" :key="'dimensions' + signature.id"></signature-dimension>
      </div>
    </template>

    <!--Multiple-->
    <div class="item" v-else-if="getSignatures.length > 0">
      <div class="actions">
        <div class="action mb-3">
          <div class="custom-control custom-checkbox">
            <input
              id="check0"
              @change="checkAll($event.target.checked)"
              :checked="allChecked"
              type="checkbox"
              class="custom-control-input"
            >
            <label class="custom-control-label" for="check0"></label>
          </div>
        </div>
        <actions :signature-id="getSignatures[0].id" :multiple="false"></actions>
      </div>
      <div class="svg">
        <svg :ref="getSignatures[0].id"></svg>
        <slider :signature-id="getSignatures[0].id"></slider>
      </div>
      <signature-dimension :signature-id="getSignatures[0].id" :dimensions="dimensions"></signature-dimension>
    </div>

    <!--Tooltip-->
    <div class="tip"></div>
  </div>
</template>

<script>
import hexToHSL from "../Converter/hexToHSL";
import hslLighten from "../Converter/hslLighten";
import EventBus from './eventBus';
import Angle from '../Calculator/angle';
import Actions from './Actions';
import Slider from './Slider';
import SignatureDimension from '../Details/SignatureDimension';

export default {
  components: {Actions, Slider, SignatureDimension},
  props: ['selected', 'options', 'checked', 'allChecked', 'selectedPoints', 'svgAutoSize'],
  data() {
    return {
      pressureFactor: 5,
      pressureExtra: 0.05,
      pressureHover: 0.08,
      tooltip: null,
      svgWidth: null,
      svgHeight: null,
      d3Line: null,
      signatureState: {},
      player: {},
      dimensions: {}
    }
  },

  watch: {
    selected: function (newData, oldData) {
      this.clearDimension()
      this.$forceUpdate();

      if (this.checked.length > 0) {
        this.checkAll();
      }
    },
    options: {
      handler(newData, oldData) {
        this.$forceUpdate();
      },
      deep: true
    },
    'options.seperate': function () {
      this.clearDimension()
    },
    checked: function (newData, oldData) {
      this.$emit('all-checked-event', this.getSignatures.length === this.checked.length)
    },
  },

  updated() {
    // Tooltip Init
    this.tooltip = d3.select(".tip");

    // Init Player Options
    this.getSignatures.forEach((signature) => {
      if (!this.player.hasOwnProperty(signature.id)) {
        this.$set(this.player, signature.id, {
          velocityFactor: 1,
          sliderMin: signature.biometricData[0].time,
          sliderMax: signature.biometricData[signature.biometricData.length - 1].time,
          slider: signature.biometricData[signature.biometricData.length - 1].time,
          play: false,
          repeat: false
        });
      }
    });

    // Draw
    if (this.selected.length > 0) {
      if (this.options.seperate) {
        this.drawMultiple();
      } else {
        this.drawSingle();
      }
    }
  },

  computed: {
    getSignatures() {
      let signatures = [];
      this.selected.map((document) => {
        document.signatures.map((s) => {
          // Clone
          let signature = JSON.parse(JSON.stringify(s));

          if (this.svgAutoSize) {
            // Reset X|Y to 0 Point
            this.signatureState[signature.id] = {};
            this.signatureState[signature.id].minY = Math.min(...signature.biometricData.map((d) => d.y - ((d.pressure + this.pressureExtra) * this.pressureFactor)));
            this.signatureState[signature.id].minX = Math.min(...signature.biometricData.map((d) => d.x - ((d.pressure + this.pressureExtra) * this.pressureFactor)));
          }

          // Hex Color to HSL Convert
          if (signature.color.charAt(0) === '#') {
            signature.color = hexToHSL(signature.color);
          }

          let start = false;
          let colorIndex = -1;
          let colorGroup = this.$parent.colors.filter((colors) => {
            return colors.find(color => color.color === signature.color)
          })[0]

          signature.biometricData.map((d) => {
            // Reset X|Y
            d.x -= this.signatureState[signature.id].minX;
            d.y -= this.signatureState[signature.id].minY;

            // Set Hover
            if (!d.hasOwnProperty('is_hover')) {
              d.is_hover = (d.pressure + this.pressureExtra) < this.pressureHover;
            }

            // Set Point Group
            if (!d.is_hover && !start) {
              start = true;

              if (colorIndex >= (colorGroup.length - 1)) {
                colorIndex = 0;
              }

              colorIndex++;
            } else if (d.is_hover) {
              start = false;
            }

            // Point Custom Color
            d.color = colorGroup[colorIndex === -1 ? 0 : colorIndex].color;

            // Calculate Stroke Width
            d.pressureCalc = !d.is_hover ? (d.pressure + this.pressureExtra) * this.pressureFactor : 0;
          })

          // Add Document Info to Signature
          signature.document = document.name;

          // Set Checked Data
          signature.checked = false;

          signatures.push(signature);
        })
      })

      return signatures;
    }
  },

  created() {
    // Extend D3 Library
    d3.selection.prototype.lastData = function() { return this.data()[this.size() - 1] || null};
    d3.selection.prototype.firstData = function() { return this.data()[0] || null };
  },

  methods: {
    /**
     * Draw
     */
    drawMultiple() {
      this.getSignatures.forEach((signature) => {
        this.draw(d3.select(this.$refs[signature.id][0]), [signature])
      })
    },

    drawSingle() {
      this.draw(d3.select(this.$refs[this.getSignatures[0].id]), this.getSignatures)
    },

    /**
     * Draw Signature
     */
    draw(svg, signatures) {
      // Clear SVG
      svg.selectAll("*").remove();

      // Calculate Max Width|Height
      if (this.svgAutoSize) {
        this.svgWidth = Math.max(...signatures.map((data) => this.getMaxWidth(data.biometricData)));
        this.svgHeight = Math.max(...signatures.map((data) => this.getMaxHeight(data.biometricData)));
      }

      this.d3Line = d3.line()
        .x((d) => d.x)
        .y((d) => d.y)
        .curve(d3.curveCatmullRom.alpha(1));

      // Draw Signature
      signatures.forEach((data) => {
        this.player[data.id].path = svg
          .attr("viewBox", `0 0 ${this.svgWidth} ${this.svgHeight}`)
          .append('g')
          .selectAll('path')
          .data(data.biometricData)
          .enter()
          .append('path')
          .attr("stroke-linecap", "round")
          .attr("stroke-opacity", 1)
          .attr("stroke-width", (d) => {
            // View Hover Data
            if (this.options.hover && d.is_hover) {
              return (.25 + this.pressureExtra) * this.pressureFactor;
            }

            return d.pressureCalc;
          })
          .style("stroke", (d) => {
            // View Hover Data
            if (this.options.hover && d.is_hover) {
              return '#CCCCCC';
            }

            // View Signature Data
            if (this.options.viewSignature) {
              return hslLighten(d.color, 100 - (d.pressure * 100));
            }

            return '#FFFFFF';
          })
          .attr('d', (d, i) => {
            if (data.biometricData[i + 1]) {
              let midPoint = {
                x: (d.x + data.biometricData[i + 1].x) / 2,
                y: (d.y + data.biometricData[i + 1].y) / 2
              }

              return 'M' + [
                d.x, d.y,
                midPoint.x, midPoint.y,
                data.biometricData[i + 1].x, data.biometricData[i + 1].y
              ].join(',');
            }

            return this.d3Line([d]);
          })
          .attr("stroke-dasharray", function(d, i) {
            return this.getTotalLength();
          })

          .attr("T", 1);

        // Draw Direction
        this.drawDirection(svg);

        // Draw Circle to Points
        this.drawCircleToPoints(svg, data);

        // Draw Point Dimensiont
        this.drawPointDimension(svg, data);
      })
    },

    /**
     * Draw Circle To Path Points
     */
    drawCircleToPoints(svg, signature) {
      let v = this;
      let circles = svg.append('g');
      circles.selectAll(".point").data(signature.biometricData)
        .enter().append("circle")
        .attr("r", 3.5)
        .attr("class", (d, index) => {
          let c = '';

          // Hover & View Point Class
          if (this.options.viewPoint) {
            if ((!this.options.hover && d.is_hover)
              || (!this.options.viewSignature && !d.is_hover)) {
              c += 'hidden-point';
            }
          } else {
            c += 'hidden-point';
          }

          // Selected Signature
          if (v.selectedPoints.hasOwnProperty(signature.id)) {
            if ((v.selectedPoints[signature.id][0] && v.selectedPoints[signature.id][0].pIndex === index) ||
              (v.selectedPoints[signature.id][1] && v.selectedPoints[signature.id][1].pIndex === index)) {
              c += ' selected-point'
            }
          }

          return c;
        })
        .attr("transform", (d) => `translate(${d.x} ${d.y})`)
        .on('mouseover', function (d) {
          // View Tooltip
          v.tooltip.transition().duration(400).style("opacity", .75).style("display", "block");
          v.tooltip.html(v.renderTooltipTemplate(d))
            .style("left", `${d3.event.pageX + 10}px`)
            .style("top", `${d3.event.pageY + 10}px`);

          // Trigger Event
          EventBus.$emit('onPointHover' + signature.id, {'el': this, 'point': d})
        })
        .on('mouseout', (d) => {
          v.tooltip.transition().duration(300).style("opacity", 0).on("end", () => {
            v.tooltip.style("display", "none")
          })
        })
        .on('click', function (d, index) {
          if (!v.selectedPoints.hasOwnProperty(signature.id)) {
            v.$set(v.selectedPoints, signature.id, []);
          }

          // Reset Points
          if (v.selectedPoints[signature.id].length === 2) {
            circles.selectAll('.selected-point').attr('style', null).style('stroke', null).attr("class", null);
            v.$delete(v.selectedPoints, signature.id)
            return;
          }

          // Break for Same Point
          if (v.selectedPoints[signature.id].length === 1 && v.selectedPoints[signature.id][0].pIndex === index) {
            return;
          }
          v.selectedPoints[signature.id].push({el: this, pIndex: index, d: d});

          // Sort for Index
          v.selectedPoints[signature.id].sort((a, b) => {
            return a.pIndex - b.pIndex
          })

          d3.select(this)
            .style('fill', hslLighten(signature.color, 50))
            .style('stroke', hslLighten(signature.color, 50))
            .attr("class", "selected-point");
        })
        .style('fill', (d, index) => {
          if (v.selectedPoints.hasOwnProperty(signature.id)) {
            if ((v.selectedPoints[signature.id][0] && v.selectedPoints[signature.id][0].pIndex === index) ||
              (v.selectedPoints[signature.id][1] && v.selectedPoints[signature.id][1].pIndex === index)) {
              return hslLighten(signature.color, 50)
            }
          }
        })
        .style('stroke', (d, index) => {
          if (v.selectedPoints.hasOwnProperty(signature.id)) {
            if ((v.selectedPoints[signature.id][0] && v.selectedPoints[signature.id][0].pIndex === index) ||
              (v.selectedPoints[signature.id][1] && v.selectedPoints[signature.id][1].pIndex === index)) {
              return hslLighten(signature.color, 50)
            }
          }
        })
    },

    /**
     * Draw Signature Direction
     */
    drawDirection(svg) {
      if (this.options.direction) {
        svg.attr("marker-mid", "url(#direction)");
        svg.append("svg:defs").append("svg:marker")
          .attr("id", "direction")
          .attr("refX", 2)
          .attr("refY", 2)
          .attr("markerWidth", 7)
          .attr("markerHeight", 6)
          .attr("markerUnits", "userSpaceOnUse")
          .attr("orient", "auto")
          .append("path")
          .attr("d", "M 0 0 6 2 0 4 1 2")
          .style("fill", "rgba(255,255,255,.8)");
      }
    },

    addTransition(signatureId, path, currentTime, velocityFactor) {
      let v = this;

      path
        .attr("stroke-opacity", 0)
        .transition()
        .duration(0)
        .attr("stroke-opacity", 1)
        .attr("T", 1)
        .attr("stroke-dashoffset", 0)
        .delay((d) => (d.time - currentTime) / velocityFactor)
        .on('start', function (d, i, e) {
          if (!v.player[signatureId].play) {
            v.player[signatureId].play = true;
          }
        })
        .on('end', function(d, i, e) {
          v.player[signatureId].slider = d.time

          if (e.length === i + 1) {
            v.player[signatureId].play = false;
            if (v.player[signatureId].repeat) {
              v.playerPlay(signatureId);
            }
          }
        })
    },

    playerPlay(signatureId) {
      let ended = this.player[signatureId].path.filter('path:last-child').attr('T');
      if (parseInt(ended) === 1) {
        this.playerStop(signatureId);
      }

      let data = this.player[signatureId].path.filter('path[T="1"]').lastData();

      this.addTransition(
        signatureId,
        this.player[signatureId].path.filter('path[T="0"]'),
        data ? data.time : this.player[signatureId].path.firstData().time,
        this.player[signatureId].velocityFactor
      );
    },

    playerStop(signatureId) {
      this.player[signatureId].play = false;
      this.player[signatureId].slider = this.player[signatureId].sliderMin;
      this.player[signatureId].path
        .interrupt()
        .attr("T", 0)
        .attr("stroke-opacity", 0)
        .attr("stroke-dashoffset", function(d, i) {
          return this.getTotalLength();
        })
    },

    playerPause(signatureId) {
      this.player[signatureId].play = false;
      this.player[signatureId].path.interrupt();
    },

    playerRepeat(signatureId, status) {
      this.player[signatureId].repeat = status ?? !this.player[signatureId].repeat;
    },

    playerVelocity(signatureId, increase) {
      if (increase) {
        this.player[signatureId].velocityFactor += 0.25;
      } else {
        if (this.player[signatureId].velocityFactor <= 0.25){
          return;
        }
        this.player[signatureId].velocityFactor -= 0.25;
      }

      this.playerPause(signatureId);
      this.playerPlay(signatureId);
    },

    /**
     * Draw Angle to Two Point
     */
    drawAngle(signatureId) {
      let svg = this.options.seperate ? d3.select(this.$refs[signatureId][0]) : d3.select(this.$refs[signatureId]);
      svg.select('.angles').remove();
      let angles = svg.append('g').attr("class", "angles");
      let defs = svg.append("defs");

      // Crosshair Glow
      let glowfilter = defs.append("filter").attr("id", "glow");
      glowfilter.append("feGaussianBlur")
        .attr("stdDeviation", 1)
        .attr("result", "coloredBlur");
      let glowMerge = glowfilter.append("feMerge");
      glowMerge.append("feMergeNode").attr("in", "coloredBlur");
      glowMerge.append("feMergeNode").attr("in", "SourceGraphic");

      // Angle Dropshadow
      let angleFilter = defs.append("filter").attr("id", "drop-shadow").attr("height", "130%");
      angleFilter.append("feGaussianBlur")
        .attr("in", "SourceAlpha")
        .attr("stdDeviation", 1)
        .attr("result", "blur");
      angleFilter.append("feOffset")
        .attr("in", "blur")
        .attr("dx", 0)
        .attr("dy", 0)
        .attr("result", "offsetBlur");
      let merge = angleFilter.append("feMerge");
      merge.append("feMergeNode").attr("in", "offsetBlur")
      merge.append("feMergeNode").attr("in", "SourceGraphic");

      // Draw
      let margin = 40;
      let items = this.options.seperate ? [[signatureId, this.selectedPoints[signatureId]]] : Object.entries(this.selectedPoints)
      for (const [key, points] of items) {
        let mX = points[0].d.x - points[1].d.x,
          mY = points[0].d.y - points[1].d.y;
        let angle = Angle.atan2Degree(mX, mY);

        // Crosshair
        angles
          .append("path")
          .attr("stroke-width", 1)
          .attr("stroke-linecap", "round")
          .attr("stroke", "red")
          .attr("fill", "none")
          .attr("d", this.d3Line([
            {x: points[0].d.x, y: points[0].d.y - margin},
            {x: points[0].d.x, y: points[0].d.y + margin},
            {x: points[0].d.x, y: points[0].d.y + margin}, // Add Point
            {x: points[0].d.x, y: points[0].d.y},
            {x: points[0].d.x, y: points[0].d.y}, // Add Point
            {x: points[0].d.x, y: points[0].d.y}, // Add Point
            {x: points[0].d.x - margin, y: points[0].d.y},
            {x: points[0].d.x - margin, y: points[0].d.y}, // Add Point
            {x: points[0].d.x + margin, y: points[0].d.y},
          ]))
          .attr("stroke-dasharray", function () {
            return this.getTotalLength() + ' ' + this.getTotalLength();
          })
          .attr("stroke-dashoffset", function () {
            return this.getTotalLength();
          })
          .transition().duration(1000).attr("stroke-dashoffset", 0)
          .style("filter", "url(#glow)");

        // Angle Line
        let line = angles.append('line')
          .attr("x1", points[0].d.x).attr("y1", points[0].d.y)
          .attr("x2", points[1].d.x).attr("y2", points[1].d.y)
          .attr("stroke", "white")
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", function () {
            return this.getTotalLength() + ' ' + this.getTotalLength();
          })
          .attr("stroke-dashoffset", function () {
            return this.getTotalLength();
          })
          .transition().duration(1000).attr("stroke-dashoffset", 2)
          .style("filter", "url(#drop-shadow)");

        // Angle Text BG Filter
        let textFilter = angles.append("filter").attr("id", "text-bg");
        textFilter.attr('x', -.06)
        textFilter.attr('y', -.05)
        textFilter.attr('width', 1.12)
        textFilter.attr('height', 0)
        textFilter.transition().duration(1000).attr('height', 1.1)
        textFilter.append('feFlood').attr('flood-color', 'rgba(249,77,77,.8)');
        textFilter.append('feComposite').attr("in", "SourceGraphic").attr('operator', 'over')

        // Angle Text
        angles.append("text")
          .attr("x", points[0].d.x < 40 ? points[0].d.x + 5 : points[0].d.x - 5)
          .attr("y", points[0].d.y < 40 ? points[0].d.y + 10 : points[0].d.y - 6)
          .attr('text-anchor', points[0].d.x < 40 ? 'start' : 'end')
          .attr('fill', '#FFF')
          .style('font-size', () => this.options.seperate ? '10px' : '7px')
          .attr('filter', 'url(#text-bg)')
          .text(angle.toFixed(2) + '°')
          .style('opacity', 0)
          .transition()
          .duration(1000)
          .style('opacity', 1);
      }
    },

    /**
     * Draw Dimensions
     */
    drawDimension(signatureId) {
      // Check Points
      if (!this.isCheckedAnglePoint(signatureId)) {
        return;
      }

      // Init Points
      if (!this.options.seperate) {
        Object.entries(this.selectedPoints).forEach(([key, item]) => {
          this.dimensions[signatureId].push(item);
        })

        return;
      }

      this.dimensions[signatureId].push(this.selectedPoints[signatureId]);
    },

    /**
     * Dimension onHover Event
     *
     * @param svg
     * @param signature
     */
    drawPointDimension(svg, signature) {
      EventBus.$on('onDimensionPointEnter' + signature.id, ({point}) => {
        let d = {
          width: Math.abs(point[0].d.x - point[1].d.x),
          height: Math.abs(point[0].d.y - point[1].d.y),
          x: Math.min(point[0].d.x, point[1].d.x),
          y: Math.min(point[0].d.y, point[1].d.y),
          offset: 10
        }

        // Clear & Draw RECT
        let rectArea = svg.append('g').attr('class', 'dimension' + signature.id);
        rectArea.append('rect')
          .attr('x', d.x)
          .attr('y', d.y)
          .attr('rx',2)
          .attr('ry', 2)
          .attr("stroke-width", 0)
          .attr('stroke', 'rgba(0,0,0,0.5)')
          .attr('fill', 'rgba(0,0,0,.2)')
          .transition().duration(500)
          .attr('width', d.width)
          .attr('height', d.height)

        // Rect Size
        rectArea.append("text").attr("x", d.x + d.width / 2).attr("y",d.y - d.offset / 2).style("font-size", "80%").text(Math.round(d.width)).style("text-anchor", "middle").attr("opacity", 0).transition().duration(500).attr("opacity", 1); // Top
        rectArea.append("text").attr("x", d.x + d.width / 2).attr("y", d.y + d.height + d.offset).style("font-size", "80%").text(Math.round(d.width)).style("text-anchor", "middle").attr('alignment-baseline', 'central').attr("opacity", 0).transition().duration(500).attr("opacity", 1); // Bottom
        rectArea.append("text").attr("x", d.x - d.offset / 2).attr("y", d.y + d.height / 2).style("font-size", "80%").text(Math.round(d.height)).style("text-anchor", "end").attr('alignment-baseline', 'central').attr("opacity", 0).transition().duration(500).attr("opacity", 1); // Left
        rectArea.append("text").attr("x", d.x + d.width + d.offset / 2).attr("y", d.y + d.height / 2).style("font-size", "80%").text(Math.round(d.height)).style("text-anchor", "start").attr('alignment-baseline', 'central').attr("opacity", 0).transition().duration(500).attr("opacity", 1); // Right
      });

      EventBus.$on('onDimensionPointLeave' + signature.id, ({point}) => {
        svg.select('.dimension' + signature.id).remove();
      });
    },

    /**
     * Clear Dimension Points
     */
    clearDimension() {
      Object.entries(this.dimensions).forEach(([signatureId, signature]) => {
        EventBus.$off('onDimensionPointEnter' + signatureId);
        EventBus.$off('onDimensionPointLeave' + signatureId);
      })
      this.dimensions = {};
      this.getSignatures.forEach((signature) => {
        this.$set(this.dimensions, signature.id, []);
      })
    },

    isCheckedAnglePoint: function (signatureId) {
      if (!this.options.seperate) {
        return this.$parent.isComparePoints;
      }
      return this.selectedPoints.hasOwnProperty(signatureId) && this.selectedPoints[signatureId].length === 2
    },

    /**
     * Get SVG Max Width
     */
    getMaxWidth(data) {
      return Math.max(...data.map((d) => d.x + (d.pressure + this.pressureExtra) + this.pressureFactor))
    },

    /**
     * Get SVG Max Height
     */
    getMaxHeight(data) {
      return Math.max(...data.map((d) => d.y + (d.pressure + this.pressureExtra) + this.pressureFactor))
    },

    /**
     * Check All Signature
     */
    checkAll(event) {
      if (event) {
        this.$emit('checked-event', {'signature': this.getSignatures})
      } else {
        this.$emit('checked-event', {'signature': []})
      }
    },

    /**
     * Render Tooltip Template
     *
     * @param d
     * @returns {string}
     */
    renderTooltipTemplate(d) {
      let texts = {
        'azimuth': this.trans('Azimuth'),
        'elevation': this.trans('Elevation'),
        'pressure': this.trans('Pressure'),
        'hover': this.trans('Hover'),
      };

      return `<ul>
        <li>${texts.azimuth}: ${d.azimuth}</li>
        <li>${texts.elevation}: ${d.elevation}</li>
        <li>${texts.pressure}: ${d.pressure}</li>
        <li>${texts.hover}: ${d.is_hover}</li>
        <li>X: ${d.x}</li>
        <li>Y:${d.y}</li>
      </ul>`;
    }
  }
}
</script>

<style lang="scss">
.signature-board {
  height: 100%;
  overflow-y: scroll;

  .item {
    margin-bottom: 15px;
    display: block;
    position: relative;


    &:last-child {
      margin-bottom: 0;
    }

    .actions {
      float: left;
      position: sticky;
      top: 0;

      .action{
        margin-bottom: .1rem;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .custom-checkbox {
        //margin-left: 4px;
      }
    }

    .svg {
      border: 2px solid #e9e9e9;
      padding: 10px;
      transition: 0.15s all;
      text-align: center;
      border-radius: 2px;
      margin-left: 35px;

      &.single {
        height: 300px;
        display: flex;
        flex-direction: column;
      }

      svg {
        display: initial;
        height: 100%;
      }
    }

    &.full {
      .svg {
        height: auto;
      }
    }

    &:hover .svg {
      border-color: rgb(63, 141, 246);
    }
  }

  &.sticky {
    &:after{
      display: inline-block;
      height: calc(100% - 300px);
      content: " ";
      background: transparent;
      width: 1px;
      right: 0;
      bottom: 0;
    }
    .item {
      position: sticky;
      top: 0;
    }
  }

  circle {
    fill: #4682b4;
    opacity: .6;
    transition: .15s all;

    &:hover, &.selected-point {
      fill: #4682b4;
      cursor: pointer;
      opacity: 1 !important;
      animation-name: stroke-animation;
      animation-duration: 1.1s;
      animation-iteration-count: infinite;
    }

    &.selected-point {
      animation-name: stroke-animation-selected;
    }

    &.hidden-point {
      opacity: 0;
    }

    @keyframes stroke-animation {
      0% {
        stroke-width: 0;
        stroke: rgba(70, 130, 180, 1);
      }
      50% {
        stroke: rgba(70, 130, 180, .5);
      }
      85% {
        stroke: transparent;
      }
      100% {
        stroke-width: 15px;
      }
    }
    @keyframes stroke-animation-selected {
      90% {
        stroke: transparent;
      }
      100% {
        stroke-width: 18px;
        stroke: none;
      }
    }
  }

  .tip {
    background: rgba(0, 0, 0, .75);
    opacity: 0;
    color: #FFF;
    position: fixed;
    padding: 5px 7.5px;
    border-radius: 2px;
    font-size: .75rem;

    ul {
      list-style-type: none;
    }
  }

  /**
   * Angle Text
   */
  .angle-text{
    background: rgba(255,255,255,.75);
    font-size: 16px;
  }
}
</style>
